import axios from "axios";
import { useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { ICreateNewOrderQueue, ICreateInternalOrderRequest, ICreateInternalOrderResponse, IRpeRecoveryActionResult, IRpeRecoveryDiagnosis, IRpeRecoveryError, ISendHtmlEmailInput, IShopifyVariant, IUnfulfilledAlertRecipient } from "./types";
import { environment } from "../../environment";



type PushOrderQueue = (info: ICreateNewOrderQueue) => Promise<void>;

type SendHtmlEmail = (input: ISendHtmlEmailInput) => Promise<boolean>;

type CreateInternalOrder = (request: ICreateInternalOrderRequest) => Promise<ICreateInternalOrderResponse | null>;

type GetProductVariants = (productId: string, brandId: number) => Promise<IShopifyVariant[] | null>;

type GetUnfulfilledAlertRecipient = () => Promise<IUnfulfilledAlertRecipient | null>;

type UpdateUnfulfilledAlertRecipient = (email: string) => Promise<boolean>;

type RpeRecoveryResult<T> = { ok: true; data: T } | { ok: false; error: IRpeRecoveryError };

type UseAdminToolsKeyFunctions = {
    pushOrderQueue: PushOrderQueue;
    /** Why an order's print file is missing and what regenerating would do. */
    diagnoseRpeRecovery: (orderNumber: string) => Promise<RpeRecoveryResult<IRpeRecoveryDiagnosis>>;
    /** Ask the file listener for the missing print files; `force` overrides the in-flight guard. */
    regenerateRpe: (orderNumber: string, force?: boolean) => Promise<RpeRecoveryResult<IRpeRecoveryActionResult>>;
    /** Retry the queued order now instead of waiting for its 30-minute retry. */
    retryOrderNow: (orderNumber: string) => Promise<RpeRecoveryResult<IRpeRecoveryActionResult>>;
    sendHtmlEmail: SendHtmlEmail;
    createInternalOrder: CreateInternalOrder;
    getProductVariants: GetProductVariants;
    getUnfulfilledAlertRecipient: GetUnfulfilledAlertRecipient;
    updateUnfulfilledAlertRecipient: UpdateUnfulfilledAlertRecipient;
    getErrorReportRecipients: () => Promise<string[]>;
    updateErrorReportRecipients: (emails: string[]) => Promise<boolean>;
}

type UseAdminTools = [
    UseAdminToolsKeyFunctions
];

export const useAdminTools = (): UseAdminTools => {
    const [, authToken] = useLogin();
    const [, setSnackbar] = useSnackbar();

    const pushOrderQueue = async (info: ICreateNewOrderQueue) => {
        const response = await axios.post(`${environment.ordersUrl}/push-order`, info, {
            headers: {
                'X-Wallmates-Auth': authToken
            },
            validateStatus: () => true
        });

        if (response.status === 409) {
            setSnackbar({
                show: true,
                snackbarLevel: "error",
                text: response.data?.message || 'Order already exists in queue'
            });
        } else if (response.status === 200) {
            setSnackbar({
                show: true,
                snackbarLevel: "info",
                text: 'Order pushed to queue'
            });
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: "error",
                text: 'Error pushing order to queue'
            });
        }
    }

    // #region rpeRecoveryRequest
    // 409/422 answers are expected outcomes (file in flight, design needs fixing) and
    // carry the diagnosis as `trace`, so they are returned to the caller, not toasted.
    const rpeRecoveryRequest = async <T,>(request: () => Promise<{ status: number; data: any }>): Promise<RpeRecoveryResult<T>> => {
        try {
            const response = await request();
            if (response.status >= 200 && response.status < 300) return { ok: true, data: response.data as T };
            const error: IRpeRecoveryError = {
                status: response.status,
                message: response.data?.message || `Request failed (${response.status})`,
                diagnosis: response.data?.trace?.files ? response.data.trace as IRpeRecoveryDiagnosis : null
            };
            if (response.status === 401 || response.status === 403 || response.status >= 500) {
                setSnackbar({ show: true, snackbarLevel: 'error', text: error.message });
            }
            return { ok: false, error };
        } catch (err: any) {
            const message = err?.response?.data?.message || err?.message || 'Request failed';
            setSnackbar({ show: true, snackbarLevel: 'error', text: message });
            return { ok: false, error: { status: err?.response?.status ?? 0, message, diagnosis: null } };
        }
    };

    const rpeHeaders = { headers: { 'X-Wallmates-Auth': authToken }, validateStatus: () => true };

    const diagnoseRpeRecovery = (orderNumber: string) =>
        rpeRecoveryRequest<IRpeRecoveryDiagnosis>(() => axios.get(`${environment.ordersUrl}/rpe-recovery`, { ...rpeHeaders, params: { order_number: orderNumber } }));

    const regenerateRpe = (orderNumber: string, force: boolean = false) =>
        rpeRecoveryRequest<IRpeRecoveryActionResult>(() => axios.post(`${environment.ordersUrl}/rpe-recovery/regenerate`, { order_number: orderNumber, force }, rpeHeaders));

    const retryOrderNow = (orderNumber: string) =>
        rpeRecoveryRequest<IRpeRecoveryActionResult>(() => axios.post(`${environment.ordersUrl}/rpe-recovery/retry`, { order_number: orderNumber }, rpeHeaders));

    const sendHtmlEmail = async (input: ISendHtmlEmailInput): Promise<boolean> => {
        try {
            const response = await axios.post(`${environment.baseUrl}/v2/email/send`, input, {
                headers: {
                    'X-Wallmates-Auth': authToken
                }
            });
            if (response.status === 200) {
                setSnackbar({ show: true, snackbarLevel: 'info', text: 'Email sent' });
                return true;
            }
            return false;
        } catch (err: any) {
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to send email' });
            return false;
        }
    }

    const createInternalOrder = async (request: ICreateInternalOrderRequest): Promise<ICreateInternalOrderResponse | null> => {
        try {
            const response = await axios.post(`${environment.baseUrl}/internal-orders/create`, request, {
                headers: { 'X-Wallmates-Auth': authToken }
            });
            if (response.status === 201) {
                setSnackbar({ show: true, snackbarLevel: 'info', text: 'Internal order created successfully' });
                return response.data as ICreateInternalOrderResponse;
            }
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to create internal order' });
            return null;
        } catch (err: any) {
            const message = err?.response?.data?.message || 'Failed to create internal order';
            setSnackbar({ show: true, snackbarLevel: 'error', text: message });
            return null;
        }
    };

    const getProductVariants = async (productId: string, brandId: number): Promise<IShopifyVariant[] | null> => {
        try {
            const response = await axios.get(`${environment.baseUrl}/internal-orders/variants`, {
                params: { product_id: productId, brand_id: brandId },
                headers: { 'X-Wallmates-Auth': authToken }
            });
            if (response.status === 200 && response.data?.data) {
                return response.data.data as IShopifyVariant[];
            }
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to fetch product variants' });
            return null;
        } catch (err: any) {
            const message = err?.response?.data?.message || 'Failed to fetch product variants';
            setSnackbar({ show: true, snackbarLevel: 'error', text: message });
            return null;
        }
    };

    const getUnfulfilledAlertRecipient = async (): Promise<IUnfulfilledAlertRecipient | null> => {
        try {
            const response = await axios.get(`${environment.v2Url}/notification-settings/unfulfilled-alert-recipient`, {
                headers: { 'X-Wallmates-Auth': authToken }
            });
            if (response.status === 200 && response.data) {
                return response.data as IUnfulfilledAlertRecipient;
            }
            return null;
        } catch (err: any) {
            console.error('Failed to get unfulfilled alert recipient', err);
            return null;
        }
    };

    const updateUnfulfilledAlertRecipient = async (email: string): Promise<boolean> => {
        try {
            const response = await axios.put(
                `${environment.v2Url}/notification-settings/unfulfilled-alert-recipient`,
                { email },
                { headers: { 'X-Wallmates-Auth': authToken } }
            );
            if (response.status === 200) {
                setSnackbar({ show: true, snackbarLevel: 'info', text: 'Alert recipient updated' });
                return true;
            }
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to update alert recipient' });
            return false;
        } catch (err: any) {
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to update alert recipient' });
            return false;
        }
    };

    // #region getErrorReportRecipients
    // Internal addresses alerted when a user submits an error report. Editable so
    // the list can change without a deploy.
    const getErrorReportRecipients = async (): Promise<string[]> => {
        try {
            const response = await axios.get(`${environment.v2Url}/notification-settings/error-report-recipients`, {
                headers: { 'X-Wallmates-Auth': authToken }
            });
            if (response.status === 200 && Array.isArray(response.data?.emails)) {
                return response.data.emails as string[];
            }
            return [];
        } catch (err: any) {
            console.error('Failed to get error report recipients', err);
            return [];
        }
    };

    // #region updateErrorReportRecipients
    const updateErrorReportRecipients = async (emails: string[]): Promise<boolean> => {
        try {
            const response = await axios.put(
                `${environment.v2Url}/notification-settings/error-report-recipients`,
                { emails },
                { headers: { 'X-Wallmates-Auth': authToken } }
            );
            if (response.status === 200) {
                setSnackbar({ show: true, snackbarLevel: 'info', text: 'Error report recipients updated' });
                return true;
            }
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to update error report recipients' });
            return false;
        } catch (err: any) {
            const message = err?.response?.data?.message || 'Failed to update error report recipients';
            setSnackbar({ show: true, snackbarLevel: 'error', text: message });
            return false;
        }
    };

    return [
        {
            pushOrderQueue,
            diagnoseRpeRecovery,
            regenerateRpe,
            retryOrderNow,
            sendHtmlEmail,
            createInternalOrder,
            getProductVariants,
            getUnfulfilledAlertRecipient,
            updateUnfulfilledAlertRecipient,
            getErrorReportRecipients,
            updateErrorReportRecipients,
        }
    ]
}


